import type { ReactNode } from 'react'
import { Pressable, ScrollView, StyleSheet } from 'react-native'
import { useSafeAreaInsets } from 'react-native-safe-area-context'

import { useFlowStepFrame } from '../hooks/useNestedSheetHeight'
import { KycExternalLinkIcon } from '../icons/KycExternalLinkIcon'
import { KycOutcomeIcon } from '../icons/KycOutcomeIcon'
import { openKycSupport } from './kycSupport'
import { Box, Text, useTheme } from '../theme/restyle'
import { KycGlassBackdrop } from './KycGlassBackdrop'

export interface FiatReceiptProps {
  /** Draws the warning-ringed glyph rather than the neutral one. */
  restricted: boolean
  title: string
  /** Label on the support link, which every receipt carries. */
  supportLabel: string
  detail?: string
  /** The footer; each receipt fills it with its own actions. */
  children?: ReactNode
}

// The copy block sits at the frame's vertical centre while the safe-area
// padding above it is not mirrored below the button.
const CENTER_COMPENSATION = 25

/**
 * The layout every terminal fiat receipt wears: a glyph, a line saying where
 * the buyer stands, a support link, and whatever action is left.
 *
 * Presentational only — it decides nothing. The wording, the glyph and the
 * actions all come from the screen that renders it, so the identity outcomes
 * and the region refusal cannot drift apart.
 */
export function FiatReceipt({
  restricted,
  title,
  supportLabel,
  detail,
  children,
}: FiatReceiptProps) {
  const theme = useTheme()
  const { height } = useFlowStepFrame()
  const insets = useSafeAreaInsets()
  const color = theme.content.elements.primary
  return (
    <Box style={{ height }}>
      <KycGlassBackdrop />
      <ScrollView
        contentContainerStyle={[
          styles.scrollContent,
          {
            paddingTop: insets.top + CENTER_COMPENSATION,
            // The frame's 24pt gap, unless the home indicator needs more.
            paddingBottom: Math.max(insets.bottom, 24),
          },
        ]}
      >
        <Box style={styles.center}>
          <Box style={styles.copy}>
            <KycOutcomeIcon restricted={restricted} />
            <Text weight="medium" style={[styles.title, { color }]}>
              {title}
            </Text>
            <Pressable
              accessibilityRole="link"
              accessibilityLabel={supportLabel}
              onPress={openKycSupport}
              style={styles.link}
            >
              <Text weight="medium" style={[styles.linkLabel, { color }]}>
                {supportLabel}
              </Text>
              <KycExternalLinkIcon color={color} />
            </Pressable>
            {detail === undefined ? null : (
              <Text style={[styles.retry, { color }]}>{detail}</Text>
            )}
          </Box>
        </Box>
        <Box style={styles.actions}>{children}</Box>
      </ScrollView>
    </Box>
  )
}

const styles = StyleSheet.create({
  scrollContent: { flexGrow: 1 },
  center: { flex: 1, justifyContent: 'center', paddingHorizontal: 32 },
  copy: { maxWidth: 296 },
  title: {
    marginTop: 20,
    maxWidth: 275,
    fontSize: 24,
    lineHeight: 26.88,
    letterSpacing: -0.48,
  },
  link: {
    // 8 + half the 44pt target's slack keeps the label on the frame's baseline.
    marginTop: 8,
    minHeight: 44,
    flexDirection: 'row',
    alignItems: 'center',
    alignSelf: 'flex-start',
    gap: 5,
  },
  linkLabel: { fontSize: 16, lineHeight: 17.92, letterSpacing: -0.32 },
  retry: {
    marginTop: 8,
    fontSize: 16,
    lineHeight: 17.92,
    letterSpacing: -0.32,
  },
  actions: { paddingHorizontal: 24, gap: 12 },
})
